import React from 'react';
import { View, Text, StyleProp, ViewStyle } from 'react-native';
import { IconButton } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { Commande, getServiceDisplayName } from '../types';
import { TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import Card from './ui/Card';
import Badge, { BadgeTone } from './ui/Badge';

const STATUT_LABELS: Record<string, string> = {
  DEVIS: 'Devis',
  PAYEE: 'Payée',
  FACTURE: 'Facturé',
  BON_COMMANDE: 'Bon de commande',
};

/** Devis → warning, Bon de commande → brand, Facture / Payée → success. */
const STATUT_TONES: Record<string, BadgeTone> = {
  DEVIS: 'warning',
  PAYEE: 'success',
  FACTURE: 'success',
  BON_COMMANDE: 'brand',
};

interface CommandeCardProps {
  commande: Commande;
  onPress: () => void;
  onDeleteCommande?: () => void;
  showEcheanceDepasseeFlag?: boolean;
  /** Optional outer style (e.g. grid cell sizing). */
  style?: StyleProp<ViewStyle>;
}

export default function CommandeCard({
  commande,
  onPress,
  onDeleteCommande,
  showEcheanceDepasseeFlag = false,
  style,
}: CommandeCardProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const totalVerse = commande.paiements.reduce((s, p) => s + Number(p.montant), 0);
  const totalTTC = Number(commande.totalTTC);
  const estFacture = commande.statut === 'FACTURE';
  const estBonCommande = commande.statut === 'BON_COMMANDE';
  const estDocumentEncaissement = estFacture || estBonCommande;
  const estPaye = commande.statut === 'PAYEE' || (totalTTC > 0 && totalVerse >= totalTTC);
  const reste = commande.statut === 'PAYEE' ? 0 : Math.max(0, totalTTC - totalVerse);
  const statutTone = STATUT_TONES[commande.statut] ?? 'neutral';
  const statutLabel = STATUT_LABELS[commande.statut] ?? commande.statut;
  const documentRef = estFacture
    ? commande.numeroFacture?.trim() || `FAC${String(commande.id).padStart(8, '0')}`
    : estBonCommande
      ? commande.numeroBonCommande?.trim() || `BC${String(commande.id).padStart(8, '0')}`
      : `Commande #${commande.id}`;
  const todayIso = new Date().toISOString().slice(0, 10);
  const estEcheanceDepassee =
    showEcheanceDepasseeFlag
    && estDocumentEncaissement
    && !!commande.dateEcheance
    && commande.dateEcheance < todayIso
    && reste > 0;
  const serviceLabels = Array.from(new Set(
    commande.lignesCommande
      .map((ligne) => getServiceDisplayName(ligne.produitService?.code, ligne.produitService?.nom))
      .filter((label) => label.length > 0)
  ));
  const serviceVenduLabel = serviceLabels.length === 0
    ? 'Service non précisé'
    : serviceLabels.length <= 2
      ? serviceLabels.join(' + ')
      : `${serviceLabels[0]} + ${serviceLabels.length - 1} autre(s)`;
  const canDelete = (commande.statut === 'DEVIS' || estDocumentEncaissement || commande.statut === 'PAYEE') && !!onDeleteCommande;

  return (
    <Card onPress={onPress} style={[styles.card, style]} accessibilityLabel={`Ouvrir ${documentRef}`}>
      <View style={styles.header}>
        <View style={styles.headerTexts}>
          <Text style={styles.id} numberOfLines={1}>{documentRef}</Text>
          <Text style={styles.client} numberOfLines={1}>
            {commande.client?.nomRaisonSociale ?? '—'}
          </Text>
        </View>
        {canDelete ? (
          <IconButton
            icon="delete-outline"
            iconColor={colors.danger}
            size={18}
            accessibilityLabel="Supprimer la commande"
            onPress={(event) => {
              if (event && typeof event.stopPropagation === 'function') {
                event.stopPropagation();
              }
              onDeleteCommande?.();
            }}
            style={styles.deleteBtn}
          />
        ) : null}
      </View>

      <View style={styles.badgeRow}>
        <Badge label={statutLabel} tone={statutTone} />
        {estPaye && <Badge label="Payé" tone="success" icon="check" />}
        {estEcheanceDepassee && <Badge label="Echéance dépassée" tone="danger" icon="clock-alert-outline" />}
      </View>

      <Text style={styles.service} numberOfLines={1}>
        <Text style={styles.serviceLabel}>Service vendu : </Text>
        {serviceVenduLabel}
      </Text>

      <View style={styles.footer}>
        <View style={styles.dateRow}>
          <MaterialCommunityIcons name="calendar-blank-outline" size={14} color={colors.textSecondary} />
          <Text style={styles.date}>{new Date(commande.date).toLocaleDateString('fr-FR')}</Text>
        </View>
        <View style={styles.footerRight}>
          {estDocumentEncaissement && !estPaye && reste > 0 && (
            <Text style={styles.reste}>Reste : {reste.toLocaleString('fr-FR')} DH</Text>
          )}
          <Text style={styles.total}>{totalTTC.toLocaleString('fr-FR')} DH</Text>
        </View>
      </View>
    </Card>
  );
}

const useStyles = makeStyles((c) => ({
  card: { marginBottom: 16 },
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
  headerTexts: { flex: 1, minWidth: 0 },
  id: { ...TYPOGRAPHY.subheading, color: c.textPrimary },
  client: { fontSize: 14, color: c.textSecondary, marginTop: 2 },
  deleteBtn: { margin: -6 },
  badgeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12 },
  service: { fontSize: 14, lineHeight: 20, color: c.textPrimary, marginTop: 12, fontWeight: '500' },
  serviceLabel: { color: c.textSecondary, fontWeight: '400' },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: c.divider,
  },
  dateRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  date: { fontSize: 13, color: c.textSecondary },
  footerRight: { flexDirection: 'row', gap: 10, alignItems: 'baseline', flexShrink: 1, flexWrap: 'wrap', justifyContent: 'flex-end' },
  reste: { fontSize: 13, color: c.danger, fontWeight: '600' },
  total: { fontSize: 16, fontWeight: '800', color: c.textPrimary },
}));
